import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import axiosInstance from "../../../../../../services/axiosInstance";
import { useParams } from "react-router-dom";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import {
  AttachmentPreviewDialog,
  AttachmentThumbnail,
  getAttachmentKind,
  isPreviewable,
  useAttachmentFiles,
} from "./AttachmentPreview";

const FilesForm = () => {
  const { id: dealId } = useParams();
  const fileInputRef = useRef(null);

  const userId = localStorage.getItem("userId");

  const [files, setFiles] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState("");
  const attachmentFiles = useAttachmentFiles();
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    if (dealId) loadFiles();
  }, [dealId]);

  // Load all files for this deal
  const loadFiles = async () => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllFiles}/${dealId}`
      );
      setFiles(res.data.files || []);
    } catch (err) {
      console.error("Error loading files:", err);
      setError("Failed to load files. Please refresh.");
    }
  };

  const addDealTimeline = async (payload) => {
    try {
      await axiosInstance.post(apiEndPointsConfig.createTimeline, payload);
    } catch (error) {
      console.error("Error creating deal timeline:", error);
    }
  };

  // File upload with proper error handling
  const uploadFile = async (file) => {
    try {
      setUploading(true);
      setError(null);
      setSuccess(false);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("userId", userId);
      formData.append("dealId", dealId); // optional, if backend expects it

      const response = await axiosInstance.post(
        `${apiEndPointsConfig.uploadFiles}/${dealId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Safely extract uploaded file info
      const uploadedFile = response.data.file || response.data || {};
      const fileName =
        uploadedFile.fileName ||
        uploadedFile.filename ||
        uploadedFile.originalName ||
        uploadedFile.originalname ||
        file.name;

      const timelinePayload = {
        dealId,
        title: "File uploaded",
        description: fileName,
        userId,
        changeTime: new Date(),
      };

      await addDealTimeline(timelinePayload);
      await loadFiles();

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Upload error", err);
      const errorMsg =
        err.response?.data?.message || "Upload failed. Please try again.";
      setError(errorMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleBrowseClick = () => {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    if (!uploading) e.preventDefault();
  };

  const handleDrop = (e) => {
    if (uploading) return;
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleFileChange = (e) => {
    if (uploading) return;
    const file = e.target.files[0];
    if (file) uploadFile(file);
  };

  // Files as preview items: S3 files open from their public URL, local-disk ones
  // load through the authenticated download route
  const items = files.map((file) => ({
    ...file,
    id: file._id,
    fetchBlob: async () =>
      (
        await axiosInstance.get(`${apiEndPointsConfig.downloadFiles}/${file._id}`, {
          responseType: "blob",
        })
      ).data,
  }));

  useEffect(() => {
    attachmentFiles.loadThumbnails(items);
  }, [files]);

  const downloadFile = (item) =>
    attachmentFiles.download(item).catch((err) => {
      console.error("Download error:", err);
      setError("Failed to download file.");
    });

  // Images, PDFs and audio open in the preview modal; anything else downloads
  const openFile = (item) =>
    isPreviewable(item) ? setPreviewId(item.id) : downloadFile(item);

  // Delete file
  const confirmDelete = (file) => {
    setSelectedFile(file);
    setDeleteModal(true);
  };

  const deleteFile = async () => {
    try {
      await axiosInstance.delete(
        `${apiEndPointsConfig.deleteFiles}/${selectedFile._id}`
      );

      setDeleteModal(false);
      setSelectedFile(null);

      const timelinePayload = {
        dealId,
        title: "File deleted",
        description: fileName,
        userId,
        changeTime: new Date(),
      };

      addDealTimeline(timelinePayload);
      loadFiles();
    } catch (err) {
      console.error("Delete file error:", err);
      setError("Failed to delete file. Please try again.");
    }
  };

  const formatSize = (bytes) =>
    bytes ? `${(bytes / 1024).toFixed(2)} KB` : "—";

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      : "";

  return (
    <Box sx={{ width: "100%", px: { xs: 0, sm: 1 }, py: 1 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Files
        </Typography>
        {files.length > 0 && (
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            {files.length} file{files.length > 1 ? "s" : ""} uploaded
          </Typography>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          File uploaded successfully!
        </Alert>
      )}

      {/* Hidden input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Upload Card */}
      <Paper
        elevation={0}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleBrowseClick}
        sx={{
          borderRadius: 2,
          border: "1px dashed #d1d5db",
          bgcolor: uploading ? "#eff6ff" : "#f9fafb",
          py: 2.5,
          px: 2.5,
          mb: 3,
          textAlign: "center",
          cursor: uploading ? "not-allowed" : "pointer",
          opacity: uploading ? 0.7 : 1,
          transition: "0.2s",
          "&:hover": !uploading
            ? {
              borderColor: "#2563eb",
              bgcolor: "#f1f5f9",
            }
            : {},
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#e0f2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {uploading ? (
              <CircularProgress size={20} sx={{ color: "#2563eb" }} />
            ) : (
              <CloudUploadIcon sx={{ fontSize: 22, color: "#2563eb" }} />
            )}
          </Box>

          {uploading ? (
            <>
              <Typography
                sx={{ fontWeight: 500, fontSize: 14, color: "#2563eb" }}
              >
                Uploading… Please wait
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: "#6b7280", maxWidth: 280 }}
              >
                Keep this page open until the upload finishes.
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 14, color: "#374151" }}>
                Drag & drop files here or{" "}
                <span style={{ color: "#2563eb", fontWeight: 600 }}>
                  browse
                </span>{" "}
                from your computer.
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
                Supported: documents, spreadsheets, images. Max 10 MB (example).
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      {/* Files List */}
      {files.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px dashed #e5e7eb",
            bgcolor: "#f9fafb",
            p: 2.5,
            textAlign: "center",
          }}
        >
          {/* <InsertDriveFileIcon
            sx={{ fontSize: 26, color: "#9ca3af", mb: 0.5 }}
          /> */}
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", fontStyle: "italic" }}
          >
            No files uploaded yet. Upload files related to this deal here.
          </Typography>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            p: 1.5,
          }}
        >
          {items.map((file, index) => (
            <Box
              key={file._id}
              sx={{
                borderRadius: 2,
                p: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: index === files.length - 1 ? 0 : 0.75,
                "&:hover": {
                  bgcolor: "#f9fafb",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                {getAttachmentKind(file) === "image" ? (
                  <AttachmentThumbnail
                    src={attachmentFiles.srcFor(file)}
                    alt={file.fileName}
                    size={48}
                    onClick={() => openFile(file)}
                  />
                ) : (
                  <Avatar
                    onClick={() => openFile(file)}
                    sx={{
                      bgcolor: "#e0f2fe",
                      color: "#2563eb",
                      width: 32,
                      height: 32,
                      cursor: "pointer",
                    }}
                  >
                    {getAttachmentKind(file) === "pdf" ? (
                      <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                    ) : getAttachmentKind(file) === "audio" ? (
                      <AudiotrackIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <DescriptionIcon sx={{ fontSize: 18 }} />
                    )}
                  </Avatar>
                )}

                <Box sx={{ minWidth: 0 }}>
                  <Tooltip title={file.fileName}>
                    <Typography
                      onClick={() => openFile(file)}
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        maxWidth: 220,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        "&:hover": { color: "#2563eb" },
                      }}
                    >
                      {file.fileName}
                    </Typography>
                  </Tooltip>

                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                    {formatSize(file.fileSize)}
                    {file.createdAt && (
                      <>
                        {" • "}
                        {formatDate(file.createdAt)}
                      </>
                    )}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isPreviewable(file) && (
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      onClick={() => setPreviewId(file.id)}
                      sx={{ borderRadius: 999, border: "1px solid #e5e7eb" }}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Download file">
                  <IconButton
                    size="small"
                    onClick={() => downloadFile(file)}
                    sx={{ borderRadius: 999, border: "1px solid #e5e7eb" }}
                  >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete file">
                  <IconButton
                    size="small"
                    onClick={() => {
                      confirmDelete(file);
                      setFileName(file.fileName);
                    }}
                    sx={{
                      borderRadius: 999,
                      border: "1px solid #fee2e2",
                      bgcolor: "#fef2f2",
                      color: "#b91c1c",
                      "&:hover": {
                        bgcolor: "#fee2e2",
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {/* File Preview */}
      <AttachmentPreviewDialog
        items={items}
        activeId={previewId}
        onChange={setPreviewId}
        onClose={() => setPreviewId(null)}
        files={attachmentFiles}
        onError={setError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 1.5,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          Delete File?
        </DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>{selectedFile?.fileName}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 1.5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setDeleteModal(false)}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            size="small"
            onClick={deleteFile}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FilesForm;
